import { type QConfigTableData } from '@exam-management/common/types';
import { QuestionTypeChip } from '@exam-management/components';
import { QConfigStatus } from '@exam-management/graphql';
import { Box, Chip, EllipsisText, Flex, styled, TableCell, TableRow } from '@shared/components';
import { WithPermission } from '@shared/permissions';

import { EDIT_QCONFIG_REQUIRED_PERMISSIONS } from '../../constants';
import { getQuestionChipType } from '../utils';

import { LOSTableRowMenu, type LOSTableRowMenuProps } from './los-table-row-menu';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderRight: '1px solid',
  borderColor: theme.palette.grey[300],
}));

export interface LOSTableRowProps extends Pick<LOSTableRowMenuProps, 'onUpdate'> {
  losNames: string[];
  data: QConfigTableData;
  status?: QConfigStatus;
}

export default function LOSTableRow(props: Readonly<LOSTableRowProps>) {
  const { losNames, data, status, onUpdate } = props;

  const { id, totalQuestions, scorePerQuestion, calculated, notCalculated, questionType, size } =
    data;

  const questionChipType = getQuestionChipType(questionType);
  const isQConfigDraft = status === QConfigStatus.Draft;

  return (
    <TableRow
      sx={{
        border: 'none',
        '& :last-of-type': {
          borderRight: 'none',
          td: {
            borderBottom: 'none',
          },
        },
      }}
    >
      <StyledTableCell
        colSpan={2}
        sx={{
          p: (theme) => theme.spacing(0),
          borderBottom: 'none',
        }}
      >
        <Flex
          sx={{ minHeight: '48px', backgroundColor: (theme) => theme.palette.background.paper }}
        >
          <Box
            sx={{
              minWidth: '48px',
              borderRight: (theme) => `1px solid ${theme.palette.grey[300]}`,
            }}
          />
          <Flex
            sx={(theme) => ({
              width: '100%',
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
              p: theme.spacing(2, 3, 2, 14),
              alignItems: 'center',
            })}
          >
            <Flex sx={{ gap: (theme) => theme.spacing(2) }}>
              <Chip
                label='LOS'
                size='small'
                sx={{
                  bgcolor: (theme) => theme.palette.grey[200],
                  fontWeight: 600,
                  width: '48px',
                }}
              />
              <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(2) }}>
                {losNames.map((losName) => (
                  <EllipsisText key={losName} variant='body2Semibold' sx={{ height: '100%' }}>
                    {losName}
                  </EllipsisText>
                ))}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </StyledTableCell>
      <StyledTableCell
        align='center'
        sx={{
          fontWeight: 600,
          alignContent: 'baseline',
        }}
      >
        {totalQuestions}
      </StyledTableCell>
      <StyledTableCell
        align='center'
        sx={{
          fontWeight: 600,
          alignContent: 'baseline',
        }}
      >
        {scorePerQuestion}
      </StyledTableCell>
      <StyledTableCell
        align='center'
        sx={(theme) => ({ alignContent: 'baseline', p: theme.spacing(3, 2) })}
      >
        {questionChipType ? <QuestionTypeChip type={questionChipType} questionSize={size} /> : '-'}
      </StyledTableCell>
      <StyledTableCell align='center' sx={{ alignContent: 'baseline' }}>
        {calculated.easy ?? '-'}
      </StyledTableCell>
      <StyledTableCell align='center' sx={{ alignContent: 'baseline' }}>
        {calculated.medium ?? '-'}
      </StyledTableCell>
      <StyledTableCell align='center' sx={{ alignContent: 'baseline' }}>
        {calculated.hard ?? '-'}
      </StyledTableCell>
      <StyledTableCell align='center' sx={{ alignContent: 'baseline' }}>
        {notCalculated.easy ?? '-'}
      </StyledTableCell>
      <StyledTableCell align='center' sx={{ alignContent: 'baseline' }}>
        {notCalculated.medium ?? '-'}
      </StyledTableCell>
      <StyledTableCell
        align='center'
        sx={{
          alignContent: 'baseline',
          borderRight: !isQConfigDraft ? 'none' : undefined,
        }}
      >
        {notCalculated.hard ?? '-'}
      </StyledTableCell>
      <WithPermission requiredPermission={EDIT_QCONFIG_REQUIRED_PERMISSIONS}>
        {isQConfigDraft ? (
          <StyledTableCell
            align='center'
            sx={{
              borderRight: 'none',
              alignContent: 'baseline',
            }}
          >
            <LOSTableRowMenu qConfigStructureId={id} onUpdate={onUpdate} />
          </StyledTableCell>
        ) : null}
      </WithPermission>
    </TableRow>
  );
}
